/**
 * Provides helper methods related to storage and search including Uri and Id helpers.
 */
export class StorageLogic {

  /**
   * Gets the idAtOwner format full ID ("[id]@[owner]") for a URI.
   * IdAtOwner format is easier to use in REST API, as part of URL than URI format.
   * @param uriString URI as ("[Potentially, a server]/[PayloadType]/[Owner]/[Id]", e.g. "/CalculationDTO/FI86POYL0009461858/16ea052e-f23f-4b72-83a9-b8f6590f0dc9")
   * @returns The owner and is in "[id]@[owner]" format.
   */
  public static getIdAtOwner(uriString: string): string {
    const parsed = this.parseUri(uriString);
    return parsed.id + "@" + parsed.owner;
  }

  /**
   * Gets the URL in local form without the server part:
   * "/[PayloadType]/[Owner]/[Id]", e.g. "/CalculationDTO/FI86POYL0009461858/16ea052e-f23f-4b72-83a9-b8f6590f0dc9".
   * @param uriString Uri that potentially has a server in the beginning (like list item uri).
   * @param forceParse If true, the URI is parsed to URI parts even if it already seems to be a local URI (starts with "/").
   * By default URI's that start with "/" are returned as is without further conversion.
   */
  public static getLocalUri(uriString: string, forceParse = false): string | undefined {
    if (!uriString) {
      return undefined;
    }
    if (uriString[0] == "/" && !forceParse) {
      return uriString;
    }
    const parsed = this.parseUri(uriString);
    return `/${parsed.payloadType}/${parsed.owner}/${parsed.id}`;
  }


  /**
   * Parses a Palkkaus storage URI in list item or container to relevant parts:
   * Payload type, Owner and Id.
   * @param uriString Uri string to parse as relevant URI parts.
   * ("[Potentially, a server]/[PayloadType]/[Owner]/[Id]", e.g. "/CalculationDTO/FI86POYL0009461858/16ea052e-f23f-4b72-83a9-b8f6590f0dc9")
   * @returns Reelvant URI parts: Payload type, Owner and Id.
   */
  public static parseUri(uriString: string): {
    /** Payload type of the container */
    payloadType: string,
    /** Owner of the Container / Partition key in the storage. */
    owner: string,
    /** Unique identifier in the storage, typically Guid. */
    id: string,
  }  {
    const localPath = (new URL(uriString, "https://www.palkkaus.fi").pathname).split("/");
    if (localPath.length < 4) {
      throw new Error("Invalid URI: " + uriString);
    }
    const result = {
      payloadType: localPath[1],
      owner: localPath[2],
      id: localPath[3],
    };
    if (!result.id || !result.owner) {
      throw new Error("Invalid URI: " + uriString);
    }
    return result;
  }

}